/** Privacy-safe browser performance and reliability aggregates.
 *
 * No URL, record id, error object, stack, query, title or user text is retained.
 * Native/monitoring adapters can consume the content-free snapshot through the
 * `exognitos:performance` event without gaining access to product content.
 */

import type { AnyRouter } from "@tanstack/react-router";

export type FrontendMetric =
  | "navigation"
  | "route_transition"
  | "inp"
  | "lcp"
  | "cls"
  | "bundle_js"
  | "bundle_css"
  | "crash"
  | "sync_replay_success"
  | "sync_replay_review"
  | "sync_replay_auth"
  | "sync_replay_pending"
  | "sync_replay_failure";

const WORKSPACES = new Set([
  "planner",
  "work",
  "memory",
  "life",
  "analysis",
  "extensions",
  "settings",
  "privacy",
  "auth",
]);
const metrics = new Map<string, number>();
const nativeMetrics = new Map<string, number>();
const observers: {
  observer: PerformanceObserver;
  callback: (entry: PerformanceEntry) => void;
}[] = [];
type InteractionSample = { duration: number; workspace: string };
const interactions = new Map<number, InteractionSample>();
let started = false;
let finalVitalsFlushed = false;
let lcpSample: InteractionSample | undefined;
let clsSupported = false;
let clsWindowStart = 0;
let clsWindowEnd = 0;
let clsWindowValue = 0;
let clsWindowWorkspace = "other";
let clsMaximum: InteractionSample | undefined;

export type NativeHealthSignal =
  | "startup"
  | "oauth_callback"
  | "file_transfer"
  | "update"
  | "package_version";
export type NativeHealthOutcome =
  | "success"
  | "failure"
  | "cancelled"
  | "current"
  | "required"
  | "missing"
  | "unknown";

export function observeNativeHealth(
  platform: "android" | "windows",
  signal: NativeHealthSignal,
  outcome: NativeHealthOutcome,
) {
  const key = [platform, signal, outcome].join(":");
  nativeMetrics.set(key, (nativeMetrics.get(key) ?? 0) + 1);
  publishSnapshot();
}

export function observeRouterTransitions(router: Pick<AnyRouter, "subscribe">) {
  let pending: { startedAt: number; workspace: string } | undefined;
  const stopBefore = router.subscribe("onBeforeNavigate", (event) => {
    if (!event.pathChanged) return;
    pending = {
      startedAt: performance.now(),
      workspace: workspaceForPath(event.toLocation.pathname),
    };
  });
  const stopRendered = router.subscribe("onRendered", (event) => {
    if (!event.pathChanged || !pending) return;
    recordClosedMetric(
      "route_transition",
      Math.max(0, performance.now() - pending.startedAt),
      pending.workspace,
    );
    pending = undefined;
    publishSnapshot();
  });
  return () => {
    pending = undefined;
    stopBefore();
    stopRendered();
  };
}

function workspaceForPath(path: string): string {
  const segment = path.split("/").filter(Boolean)[0]?.toLowerCase() ?? "other";
  const aliases: Record<string, string> = {
    planlayici: "planner",
    calisma: "work",
    hafiza: "memory",
    yasam: "life",
    analiz: "analysis",
    eklentiler: "extensions",
    ayarlar: "settings",
    gizlilik: "privacy",
    giris: "auth",
  };
  const candidate = aliases[segment] ?? segment;
  return WORKSPACES.has(candidate) ? candidate : "other";
}

function valueBucket(metric: FrontendMetric, value: number): string {
  if (metric === "crash" || metric.startsWith("sync_replay_")) {
    return value > 0 ? "event" : "none";
  }
  if (metric === "bundle_js" || metric === "bundle_css") {
    if (value <= 50 * 1_024) return "le_51200";
    if (value <= 100 * 1_024) return "le_102400";
    if (value <= 512 * 1_024) return "le_524288";
    return "overflow";
  }
  if (metric === "cls") {
    if (value <= 0.1) return "good";
    if (value <= 0.25) return "needs_improvement";
    return "poor";
  }
  for (const threshold of [50, 100, 200, 300, 600, 1_000, 3_000]) {
    if (value <= threshold) return `le_${threshold}`;
  }
  return "overflow";
}

export function observeFrontendMetric(
  metric: FrontendMetric,
  value: number,
  path = typeof window === "undefined" ? "/" : window.location.pathname,
) {
  recordClosedMetric(metric, value, workspaceForPath(path));
  publishSnapshot();
}

function recordClosedMetric(metric: FrontendMetric, value: number, workspace: string) {
  const key = [metric, workspace, valueBucket(metric, Math.max(0, value))].join(":");
  metrics.set(key, (metrics.get(key) ?? 0) + 1);
}

function publishSnapshot() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent("exognitos:performance", { detail: performanceSnapshot() }),
    );
  }
}

export function interactionToNextPaint(
  samples: readonly InteractionSample[],
): InteractionSample | undefined {
  if (!samples.length) return undefined;
  const ordered = [...samples].sort((left, right) => right.duration - left.duration);
  // INP ignores one high-latency outlier for each 50 interactions. Most page
  // visits therefore report the worst interaction; long visits use the next
  // rank for every complete group of 50.
  return ordered[Math.min(Math.floor(ordered.length / 50), ordered.length - 1)];
}

export function performanceSnapshot() {
  return {
    schema_version: 1,
    privacy: "content_free_closed_labels",
    series: [...metrics.entries()]
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([key, count]) => {
        const [metric, workspace, bucket] = key.split(":");
        return { metric, workspace, bucket, count };
      }),
    native_series: [...nativeMetrics.entries()]
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([key, count]) => {
        const [platform, signal, outcome] = key.split(":");
        return { platform, signal, outcome, count };
      }),
  };
}

function createObserver(type: string, callback: (entry: PerformanceEntry) => void) {
  if (typeof PerformanceObserver === "undefined") return false;
  if (!PerformanceObserver.supportedEntryTypes.includes(type)) return false;
  const observer = new PerformanceObserver((list) => {
    for (const entry of list.getEntries()) callback(entry);
  });
  const options = { type, buffered: true } as PerformanceObserverInit & {
    durationThreshold?: number;
  };
  if (type === "event") options.durationThreshold = 40;
  observer.observe(options);
  observers.push({ observer, callback });
  return true;
}

export function startBrowserPerformanceObservers() {
  if (started || typeof window === "undefined") return;
  started = true;

  createObserver("navigation", (entry) => observeFrontendMetric("navigation", entry.duration));
  createObserver("largest-contentful-paint", (entry) => {
    lcpSample = {
      duration: entry.startTime,
      workspace: workspaceForPath(window.location.pathname),
    };
  });
  createObserver("event", (entry) => {
    const event = entry as PerformanceEntry & { interactionId?: number };
    if (!event.interactionId) return;
    const previous = interactions.get(event.interactionId);
    if (!previous || event.duration > previous.duration) {
      interactions.set(event.interactionId, {
        duration: event.duration,
        workspace: workspaceForPath(window.location.pathname),
      });
    }
  });
  clsSupported = createObserver("layout-shift", (entry) => {
    const shift = entry as PerformanceEntry & { hadRecentInput?: boolean; value?: number };
    if (shift.hadRecentInput) return;
    const value = Math.max(0, shift.value ?? 0);
    if (entry.startTime - clsWindowEnd > 1_000 || entry.startTime - clsWindowStart > 5_000) {
      clsWindowStart = entry.startTime;
      clsWindowValue = value;
      clsWindowWorkspace = workspaceForPath(window.location.pathname);
    } else {
      clsWindowValue += value;
    }
    clsWindowEnd = entry.startTime;
    if (!clsMaximum || clsWindowValue > clsMaximum.duration) {
      clsMaximum = { duration: clsWindowValue, workspace: clsWindowWorkspace };
    }
  });
  createObserver("resource", (entry) => {
    const resource = entry as PerformanceResourceTiming;
    const pathname = new URL(resource.name, window.location.origin).pathname;
    if (pathname.endsWith(".js")) observeFrontendMetric("bundle_js", resource.transferSize);
    if (pathname.endsWith(".css")) observeFrontendMetric("bundle_css", resource.transferSize);
  });

  // Count failures only. The event/reason itself may contain private response
  // text or a rendered component stack and is intentionally never inspected.
  window.addEventListener("error", recordCrash);
  window.addEventListener("unhandledrejection", recordCrash);
  window.addEventListener("pagehide", flushPageVitals);
  document.addEventListener("visibilitychange", flushWhenHidden);
}

function recordCrash() {
  observeFrontendMetric("crash", 1);
}

function flushWhenHidden() {
  if (document.visibilityState === "hidden") flushPageVitals();
}

function flushPageVitals() {
  if (finalVitalsFlushed) return;
  for (const registration of observers) {
    for (const entry of registration.observer.takeRecords()) registration.callback(entry);
  }
  finalVitalsFlushed = true;
  const inp = interactionToNextPaint([...interactions.values()]);
  if (inp) recordClosedMetric("inp", inp.duration, inp.workspace);
  if (lcpSample) recordClosedMetric("lcp", lcpSample.duration, lcpSample.workspace);
  if (clsSupported) {
    const cls = clsMaximum ?? {
      duration: 0,
      workspace: workspaceForPath(window.location.pathname),
    };
    recordClosedMetric("cls", cls.duration, cls.workspace);
  }
  publishSnapshot();
}

export function resetFrontendMetricsForTests() {
  metrics.clear();
  nativeMetrics.clear();
  for (const registration of observers.splice(0)) registration.observer.disconnect();
  if (typeof window !== "undefined") {
    window.removeEventListener("error", recordCrash);
    window.removeEventListener("unhandledrejection", recordCrash);
    window.removeEventListener("pagehide", flushPageVitals);
    document.removeEventListener("visibilitychange", flushWhenHidden);
  }
  interactions.clear();
  finalVitalsFlushed = false;
  lcpSample = undefined;
  clsSupported = false;
  clsWindowStart = 0;
  clsWindowEnd = 0;
  clsWindowValue = 0;
  clsWindowWorkspace = "other";
  clsMaximum = undefined;
  started = false;
}
